import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { BRAND, NAV_LINKS } from "../lib/constants";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href) => {
    setOpen(false);
    if (href.startsWith("#")) {
      const el = document.querySelector(href);
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.location.href = href;
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FFFFFF]/85 border-b border-[#AC9667]/10 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between gap-6">
        <a
          href="/"
          data-testid="site-logo"
          className="flex items-center gap-3 group"
        >
          <div className="bg-[#FFFFFF] rounded-2xl p-1.5 shadow-[0_4px_16px_-6px_rgba(15,23,42,0.18)] ring-1 ring-[#AC9667]/30">
            <img
              src="https://customer-assets.emergentagent.com/job_serene-home-care-1/artifacts/tqn1isrq_IMG_20251217_193656.jpg"
              alt="Madison Peaceful Path Home Care"
              className="h-10 sm:h-12 w-auto object-contain block"
            />
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`}
              onClick={() => handleNav(l.href)}
              className="text-sm text-[#1F2937] hover:text-[#AC9667] transition-colors duration-300 relative group"
            >
              {l.label}
              <span className="absolute left-0 -bottom-1 w-0 h-px bg-[#AC9667] group-hover:w-full transition-all duration-300" />
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="/auth"
            data-testid="header-signin-link"
            className="text-sm text-[#1F2937] hover:text-[#AC9667] transition-colors"
          >
            Sign In
          </a>
          <a
            href={`tel:${BRAND.phoneRaw}`}
            data-testid="header-call-link"
            className="flex items-center gap-2 text-sm text-[#005165] hover:text-[#AC9667] transition-colors"
          >
            <Phone className="w-4 h-4" />
            <span className="font-medium">{BRAND.phone}</span>
          </a>
          <Button
            data-testid="header-intake-cta"
            onClick={() => handleNav("#contact")}
            className="bg-[#005165] hover:bg-[#003946] text-[#FFFFFF] rounded-full px-6 h-11 btn-shimmer"
          >
            Schedule Consultation
          </Button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#005165] text-[#FFFFFF]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden absolute top-full inset-x-0 bg-[#FFFFFF] border-b border-[#AC9667]/10 shadow-lg"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => handleNav(l.href)}
                className="text-left text-base text-[#005165] hover:text-[#AC9667]"
              >
                {l.label}
              </button>
            ))}
            <a
              href={`tel:${BRAND.phoneRaw}`}
              className="flex items-center gap-2 text-[#AC9667] font-medium"
            >
              <Phone className="w-4 h-4" /> {BRAND.phone}
            </a>
            <a href="/auth" data-testid="mobile-signin" className="text-base text-[#005165] hover:text-[#AC9667]">Sign In</a>
            <Button
              data-testid="mobile-intake-cta"
              onClick={() => handleNav("#contact")}
              className="bg-[#005165] text-[#FFFFFF] rounded-full h-12"
            >
              Schedule Consultation
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
